import React, { useEffect } from "react";
import { C, font } from "../styles/tokens";
import { InlineIcon } from "./InlineIcon";

/**
 * Toast Component for Email Notifications & Payment Status
 * Types: "success" | "error" | "info" | "warning"
 */
export function Toast({
  type = "info",
  title,
  message,
  actionText,
  onAction,
  onClose,
  duration = 6000,
}) {
  useEffect(() => {
    if (duration && onClose) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const getStyle = () => {
    switch (type) {
      case "success":
        return {
          background: "#08833E",
          borderColor: "#055D2C",
          icon: "check",
        };
      case "error":
        return {
          background: "#DC2626",
          borderColor: "#B91C1C",
          icon: "warning",
        };
      case "warning":
        return {
          background: "#D97706",
          borderColor: "#B45309",
          icon: "warning",
        };
      default:
        return {
          background: "#0A66C2",
          borderColor: "#084E96",
          icon: "info",
        };
    }
  };

  const style = getStyle();

  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 9999,
        minWidth: 320,
        maxWidth: 420,
        backgroundColor: style.background,
        color: "#FFFFFF",
        borderRadius: 12,
        padding: "16px 20px",
        boxShadow:
          "0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)",
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        fontFamily: font,
        animation: "slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
        border: `1px solid ${style.borderColor}`,
      }}
    >
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
      <div style={{ fontSize: 22, lineHeight: 1 }}>
        <InlineIcon name={style.icon} size={20} />
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontWeight: 700,
            fontSize: 14,
            marginBottom: 4,
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 12.5, opacity: 0.92, lineHeight: 1.45 }}>
          {message}
        </div>
        {actionText && onAction && (
          <button
            onClick={onAction}
            style={{
              marginTop: 10,
              padding: "6px 14px",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "#FFFFFF",
              border: "1px solid rgba(255, 255, 255, 0.4)",
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
            onMouseOver={(e) =>
              (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.35)")
            }
            onMouseOut={(e) =>
              (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)")
            }
          >
            {actionText}
          </button>
        )}
      </div>
      {onClose && (
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            background: "none",
            border: 0,
            color: "rgba(255, 255, 255, 0.7)",
            fontSize: 18,
            cursor: "pointer",
            padding: 0,
            lineHeight: 1,
            marginLeft: 4,
          }}
          onMouseOver={(e) => (e.currentTarget.style.color = "#FFFFFF")}
          onMouseOut={(e) =>
            (e.currentTarget.style.color = "rgba(255, 255, 255, 0.7)")
          }
        >
          <InlineIcon name="close" size={18} />
        </button>
      )}
    </div>
  );
}
